import type { Accessor, ParentComponent } from "solid-js";
import { createContext, useContext } from "solid-js";
import type { BoardItemListItemResponse } from "@/client/types.gen";

export type ClipIds = { id: string };

export type BoardDetailsSearchProps = {
	value: string;
	onSearch: (search: string) => void;
	onClear: () => void;
};

export type BoardDetailsContextValue = {
	boardId: string;
	projectId?: string;
	isOwner: Accessor<boolean>;
	canManage: Accessor<boolean>;
	clipCount: Accessor<number>;
	clipSearch: Accessor<string>;
	showDetails: Accessor<boolean>;
	setShowDetails: (showDetails: boolean) => void;
	mode: Accessor<"bulk_select" | "view">;
	setMode: (mode: "bulk_select" | "view") => void;
	selectedClipIds: Accessor<ClipIds[]>;
	setSelectedClipIds: (clipIds: ClipIds[]) => void;
	deleteClips: (clipIds: ClipIds[]) => Promise<void>;
	downloadClips: (clipIds: ClipIds[]) => Promise<void>;
	downloadAllAssets: () => Promise<void>;
	showDownloadButton: boolean;
	searchProps: BoardDetailsSearchProps;
	playVideo: (clip: BoardItemListItemResponse, index: number) => void;
	activePlayClipId: Accessor<string | undefined>;
	openMovePanel: (clipIds: ClipIds[]) => void;
};

const BoardDetailsContext = createContext<BoardDetailsContextValue>();

export const BoardDetailsProvider: ParentComponent<{
	value: BoardDetailsContextValue;
}> = (props) => {
	return (
		<BoardDetailsContext.Provider value={props.value}>
			{props.children}
		</BoardDetailsContext.Provider>
	);
};

export function useBoardDetails(): BoardDetailsContextValue {
	const ctx = useContext(BoardDetailsContext);
	if (!ctx) {
		throw new Error(
			"Board details components must be used within BoardDetailsProvider",
		);
	}
	return ctx;
}
